import React from 'react';
import { Box, Grid, Toolbar } from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardTitle, CircleIcon, FixedBox, SecondaryToolbar, Typography, UnicodeEntity } from '../../../ui-kit';
import { usePriceService } from '../../../utils/hooks/priceService';
import { useUser } from '../../../utils/hooks/auth';
import DateTimeFilter from './DateTimeFilter';
import WriteOffSummary from './WriteOffSummary';
import ProductCostSummary from './ProductCostSummary';
import InventoryCountsSummary from './InventoryCountsSummary';
import { CurrentVenueInventoryCounts, DeductionAmounts, FinishedInventoryCount, InventoryCountDetails, ProductCosts } from './types';

interface ExpensesBreakdownViewProps {
    dateFrom: Date;
    handleDateFromChange(date: MaterialUiPickersDate): void;
    dateTo: Date;
    handleDateToChange(date: MaterialUiPickersDate): void;
    deductionAmounts: DeductionAmounts;
    productCosts: ProductCosts;
    currentVenueInventoryCounts: CurrentVenueInventoryCounts;
}

const withVat = false;

const ExpensesBreakdownView = ({
    dateFrom,
    handleDateFromChange,
    dateTo,
    handleDateToChange,
    deductionAmounts,
    productCosts,
    currentVenueInventoryCounts,
}: ExpensesBreakdownViewProps) => {
    const { t } = useTranslation();
    const { priceService } = usePriceService();
    const { user: { defaultCurrency } } = useUser();
    const vatDisclaimer = withVat ? t('VAT included') : t('without VAT');

    let [
        { withoutVat: deductionsSummary },
        { withoutVat: productPrimeCosts },
    ] = [deductionAmounts, productCosts];

    if (withVat) {
        [
            { withVat: deductionsSummary },
            { withVat: productPrimeCosts },
        ] = [deductionAmounts, productCosts];
    }

    const inventoryCountDetails: InventoryCountDetails[] = [];
    let inventoryCountBalancesAmount = 0;
    currentVenueInventoryCounts.forEach(finishedInventoryCount => {
        const { id, endDate, deltas } = finishedInventoryCount as FinishedInventoryCount;
        const targetDelta = withVat ? deltas.withVat : deltas.withoutVat;
        inventoryCountBalancesAmount = priceService.sumPrices(
            inventoryCountBalancesAmount,
            targetDelta,
            defaultCurrency,
            { allowsNegativeAddends: true },
        );

        inventoryCountDetails.push({ id, endDate, delta: targetDelta });
    });

    // inventoryCountBalancesAmount delta can be either positive or negative, while the rest are always positive
    const grandTotal = inventoryCountBalancesAmount - (deductionsSummary.total + productPrimeCosts);

    const isGrandTotalPositive = grandTotal >= 0;
    return (
        <>
            <FixedBox>
                <SecondaryToolbar>
                    <DateTimeFilter
                        dateFrom={dateFrom}
                        handleDateFromChange={handleDateFromChange}
                        dateTo={dateTo}
                        handleDateToChange={handleDateToChange}
                    />
                </SecondaryToolbar>
            </FixedBox>
            <Toolbar />
            <Box my={2} />
            <Grid container justify='center' spacing={3}>
                <Grid item xs={12} sm={9} md={6}>
                    <Card>
                        <CardContent padding={3}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <CardTitle>{t('Cost of Goods Sold ({{vatDisclaimer}})', { vatDisclaimer })}</CardTitle>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box display='flex' alignItems='center' justifyContent='space-between'>
                                        <Typography variant='h5'>
                                            {t('Total')}
                                            {UnicodeEntity.Colon}
                                        </Typography>
                                        <Box display='flex' alignItems='center'>
                                            <Typography variant='h5' colorVariant={isGrandTotalPositive ? 'success' : 'error'}>
                                                {priceService.toLocaleString(grandTotal)}
                                            </Typography>
                                            <Box ml={1} />
                                            <CircleIcon
                                                isSmall
                                                color={isGrandTotalPositive ? 'success' : 'error'}
                                                iconComponent={isGrandTotalPositive
                                                    ? <ArrowUpwardIcon fontSize='inherit' />
                                                    : <ArrowDownwardIcon fontSize='inherit' />}
                                            />
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <WriteOffSummary deductionsSummary={deductionsSummary} />
                                </Grid>
                                <Grid item xs={12}>
                                    <ProductCostSummary productPrimeCosts={productPrimeCosts} />
                                </Grid>
                                <Grid item xs={12}>
                                    <InventoryCountsSummary
                                        currentVenueInventoryCounts={currentVenueInventoryCounts}
                                        inventoryCountBalancesAmount={inventoryCountBalancesAmount}
                                        inventoryCountDetails={inventoryCountDetails}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </>
    );
};

export default ExpensesBreakdownView;